import type { ApiError, FieldErrors } from './apiError.types';
import { translateError } from './errorTranslator';
import type { ErrorTranslationOptions } from './errorUtils.types';

/**
 * Extracts field-specific errors from ApiError for form validation
 * Returns a map of field names to error messages
 *
 * @param apiError - API error to extract field errors from
 * @param options - Optional translation options
 * @returns Map of field names to arrays of error messages
 */
export function extractFieldErrors(
  apiError: ApiError,
  options?: ErrorTranslationOptions,
): FieldErrors {
  const fieldErrors: FieldErrors = {};

  apiError.errors.forEach((error) => {
    if (error.field) {
      const message = options ? translateError(error, options) : error.message;

      if (!fieldErrors[error.field]) {
        fieldErrors[error.field] = [];
      }
      fieldErrors[error.field].push(message);
    }
  });

  return fieldErrors;
}

/**
 * Gets first error for a specific field
 *
 * @param apiError - API error to search
 * @param fieldName - Name of the field
 * @param options - Optional translation options
 * @returns First error message for the field or null
 */
export function getFieldError(
  apiError: ApiError,
  fieldName: string,
  options?: ErrorTranslationOptions,
): string | null {
  const fieldErrors = extractFieldErrors(apiError, options);
  const errors = fieldErrors[fieldName];
  return errors && errors.length > 0 ? errors[0] : null;
}

/**
 * Checks if ApiError contains errors for a specific field
 *
 * @param apiError - API error to check
 * @param fieldName - Name of the field
 * @returns True if field has errors
 */
export function hasFieldError(apiError: ApiError, fieldName: string): boolean {
  return apiError.errors.some((error) => error.field === fieldName);
}

/**
 * Gets all general errors (errors without field association)
 *
 * @param apiError - API error to extract general errors from
 * @param options - Optional translation options
 * @returns Array of general error messages
 */
export function getGeneralErrors(
  apiError: ApiError,
  options?: ErrorTranslationOptions,
): string[] {
  return apiError.errors
    .filter((error) => !error.field)
    .map((error) =>
      options &&
      // Do not translate ValidationError to validation error list heading when there is in fact only one error
      !(apiError.errors?.length === 1 && error?.labelKey === 'ValidationError')
        ? translateError(error, options)
        : error.message,
    );
}
